import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import {
  defaultSettings,
  type PendingWeekStartDay,
  SETTINGS_KEY,
  type SettingsRecord,
} from '~/models/settings'
import { toIsoTimestamp } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

/**
 * Read settings, materializing defaults on first use.
 *
 * Defaults are written rather than returned transiently, so the stored record
 * is always the authority and a later default change cannot silently alter a
 * user's existing configuration.
 */
export async function getSettings(
  db: SolaDatabase,
  now: Date,
): Promise<Result<SettingsRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.settings, SETTINGS_KEY)
    if (existing !== undefined) return ok(existing)

    const timestamp = toIsoTimestamp(now)
    const record = defaultSettings({
      createdAt: timestamp,
      updatedAt: timestamp,
      schemaVersion: DB_SCHEMA_VERSION,
    })

    await db.put(STORE.settings, record)
    notifyWritten(STORE.settings)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export type SettingsPatch = Partial<
  Omit<SettingsRecord, 'id' | 'createdAt' | 'updatedAt' | 'schemaVersion' | 'pendingWeekStartDay'>
> & {
  /**
   * Explicit `undefined` **clears** the pending week-start change (D-105 §8).
   *
   * The only field here that can be removed rather than only set, which is why
   * it is typed apart from the rest: promoting a change and cancelling one both
   * mean "there is nothing waiting now", and a patch that could only write a
   * value would have to store a meaningless one to say so.
   */
  readonly pendingWeekStartDay?: PendingWeekStartDay | undefined
}

export async function updateSettings(
  db: SolaDatabase,
  patch: SettingsPatch,
  now: Date,
): Promise<Result<SettingsRecord, StorageError>> {
  const current = await getSettings(db, now)
  if (!current.ok) return current

  try {
    const record = {
      ...current.value,
      ...patch,
      id: SETTINGS_KEY,
      updatedAt: toIsoTimestamp(now),
      schemaVersion: DB_SCHEMA_VERSION,
    } as SettingsRecord

    /* Written as an absent key rather than as `undefined`: IndexedDB stores the
       property either way, and a record holding an explicit `undefined` reads
       back as a field that exists and is empty — which is a different claim from
       "there is nothing waiting", and the one a later reader would get wrong. */
    if ('pendingWeekStartDay' in patch && patch.pendingWeekStartDay === undefined) {
      delete (record as { pendingWeekStartDay?: PendingWeekStartDay }).pendingWeekStartDay
    }

    await db.put(STORE.settings, record)
    notifyWritten(STORE.settings)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
